import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import SidebarCard from './SidebarCard';
import { getPageMetrics } from '../api';
import { getSiteRankings } from '../apiRankings';
import { indexMetricsBySlug, metricsForPage, isEstimated, formatMoney, formatCount } from '../pageMetrics';
import { indexSearchByPage, searchForPage, formatPosition, positionMove } from '../seo/pageSearch';
import { fmtUsd } from '../donext/doNext';

/* Same period and size as the Rankings tab and the page list, so all three
   read one cached summary. */
const SEARCH_DAYS = 28;
const SEARCH_ROWS = 100;
const QUERIES_SHOWN = 6;

const Stat = ({ label, value, sub }) => (
  <div style={{ minWidth: 0 }}>
    <div className="kpi-label" style={{ marginBottom: 2 }}>{label}</div>
    <div style={{ fontWeight: 600, fontSize: 15, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
    {sub && <div className="muted" style={{ fontSize: 11 }}>{sub}</div>}
  </div>
);

/* "Value": what this page earns now and what Google could add, so an edit
   can be judged by its upside. Both requests are site-wide and cached on the
   server; the card shows what it has as each answers. */
export default function SearchCard({ domainId, slug, open, onToggle }) {
  const [metrics, setMetrics] = useState(undefined); // undefined = loading
  const [search, setSearch] = useState(undefined);

  useEffect(() => {
    let cancelled = false;
    setMetrics(undefined);
    setSearch(undefined);
    getPageMetrics(domainId).then((m) => { if (!cancelled) setMetrics(m || null); }).catch(() => { if (!cancelled) setMetrics(null); });
    getSiteRankings(domainId, SEARCH_DAYS, SEARCH_ROWS).then((r) => { if (!cancelled) setSearch(r || null); });
    return () => { cancelled = true; };
  }, [domainId]);

  const page = useMemo(() => ({ slug }), [slug]);
  const m = useMemo(() => metricsForPage(indexMetricsBySlug(metrics && metrics.pages), page), [metrics, page]);
  const g = useMemo(() => searchForPage(indexSearchByPage(search), page), [search, page]);
  const move = positionMove(g.position, g.prevPosition);
  const estimated = isEstimated(m);
  const searchFailed = search === null || (search && search.error);
  const shown = g.queries.slice(0, QUERIES_SHOWN);

  const badge = g.upsideUsd >= 0.01
    ? <span className="small" style={{ color: 'var(--color-success)', fontWeight: 600 }}>+{fmtUsd(g.upsideUsd)}/mo</span>
    : null;

  return (
    <SidebarCard id="search" title="Value" open={open} onToggle={onToggle} badge={badge}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '10px 12px', marginBottom: 10 }}>
        <Stat label="Views (30d)" value={metrics === undefined ? '…' : formatCount(m.hits)} />
        <Stat
          label="Earnings (30d)"
          value={metrics === undefined ? '…' : `${formatMoney(m.earnings)}${estimated && m.earnings != null ? '°' : ''}`}
          sub={estimated && m.earnings != null ? '° estimated from impressions' : null}
        />
        <Stat
          label={`Google position (${SEARCH_DAYS}d)`}
          value={search === undefined ? '…' : formatPosition(g.position)}
          sub={move ? <span style={{ color: move.up ? 'var(--color-success)' : 'var(--color-danger)' }}>{move.label} places</span> : null}
        />
        <Stat
          label={`Google clicks (${SEARCH_DAYS}d)`}
          value={search === undefined ? '…' : formatCount(g.clicks)}
          sub={g.impressions ? `${formatCount(g.impressions)} impressions` : null}
        />
      </div>

      {search === undefined ? null : searchFailed ? (
        <div className="muted small">Google search data could not be loaded.</div>
      ) : g.queries.length === 0 ? (
        <div className="muted small">
          Google has not shown this page for any tracked query in the last {SEARCH_DAYS} days.
        </div>
      ) : (
        <>
          {g.best ? (
            <div className="small" style={{ marginBottom: 8 }}>
              Moving its queries into the top 3 could bring <strong>~{formatCount(Math.round(g.upsideClicks))} more clicks a month</strong>
              {g.upsideUsd >= 0.01 ? <> (<strong>~{fmtUsd(g.upsideUsd)}/mo</strong>)</> : null}.
              Best to push: <strong>“{g.best.query}”</strong>, now {formatPosition(g.best.position)}.
            </div>
          ) : (
            <div className="muted small" style={{ marginBottom: 8 }}>Its queries are already in the top 3 or too deep to estimate.</div>
          )}
          <table className="data-table" style={{ fontSize: 12, width: '100%', tableLayout: 'fixed' }}>
            <thead>
              <tr>
                <th>Query</th>
                <th className="num" style={{ width: 44 }} title="Average position">Pos</th>
                <th className="num" style={{ width: 64 }} title="Extra clicks a month at the top 3">+clicks</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((q) => (
                <tr key={q.query} style={g.best && q.query === g.best.query ? { background: '#f0fdf4' } : undefined}>
                  <td style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      title={`${q.query}: ${formatCount(q.impressions)} impressions, ${formatCount(q.clicks)} clicks`
                        + (q.est_monthly_searches ? `, ~${formatCount(q.est_monthly_searches)} searches a month` : '')}>
                    {q.query}
                  </td>
                  <td className="num">{formatPosition(q.position)}</td>
                  <td className="num">{q.gain_top3_clicks > 0 ? `+${formatCount(Math.round(q.gain_top3_clicks))}` : <span className="muted">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {g.queries.length > QUERIES_SHOWN && (
            <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
              {g.queries.length - QUERIES_SHOWN} more quer{g.queries.length - QUERIES_SHOWN === 1 ? 'y' : 'ies'} in Rankings.
            </div>
          )}
        </>
      )}
      <div style={{ marginTop: 8 }}>
        <Link className="small" to={`/domain/${encodeURIComponent(domainId)}/rankings?tab=pages`}>Rankings →</Link>
      </div>
    </SidebarCard>
  );
}
